import React from "react";
import { useHandleClickOutside, useScroll } from "oolib";
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { StyledOptionsLightboxWrapper } from "../../styled";

export const DynamicOptionsLightboxShell = ({
  onClickOutside,
  onClose,
  ref_anchorPoint,
  xOffsetFromAnchorPoint = 4,

  children,
  isMobile,

  width = {
    desktop: 250,
    mobile: 300,
  },
  overflow = "auto",
}) => {
  const ref_optionsLightboxWrapper = useRef(null);
  useHandleClickOutside(ref_optionsLightboxWrapper, () => {
    onClickOutside && onClickOutside();
  });

  const getBoundsOfAnchorPoint = () =>
    ref_anchorPoint?.current?.getBoundingClientRect();

  // console.log({ ref_anchorPointWidth: ref_anchorPoint.current });

  useScroll(() => {
    setBoundsOfAnchorPoint(getBoundsOfAnchorPoint());
    getDimensions();
  });

  const [boundsOfAnchorPoint, setBoundsOfAnchorPoint] = useState(
    getBoundsOfAnchorPoint()
  );

  const getDimensions = useCallback(() => {
    const viewPortHeight = window.innerHeight;
    const centerOfScreen = viewPortHeight / 2;
    const topSpace = 25;
    const bottomSpace = 25;

    // Check if element is above the viewport (scrolled past top)
    if (boundsOfAnchorPoint.bottom < 0) {
      // Element is above viewport, position at top with padding
      // Ensure we leave space at both top and bottom
      return {
        maxHeight: viewPortHeight - (topSpace + bottomSpace),
        top: topSpace,
        bottom: "auto",
      };
    }

    // Check if element is below the viewport (scrolled past bottom)
    if (boundsOfAnchorPoint.top > viewPortHeight) {
      // Element is below viewport, position at bottom with padding
      // Ensure we leave space at both top and bottom
      return {
        maxHeight: viewPortHeight - (topSpace + bottomSpace),
        top: "auto",
        bottom: bottomSpace,
      };
    }

    // Element is visible on screen - follow original logic
    if (boundsOfAnchorPoint.top < centerOfScreen) {
      // Element is in top half of screen
      // Ensure we leave space at both top and bottom
      const height =
        viewPortHeight -
        bottomSpace -
        Math.max(boundsOfAnchorPoint.top, topSpace);

      return {
        maxHeight: height,
        top: Math.max(boundsOfAnchorPoint.top, topSpace),
        bottom: "auto",
      };
    } else {
      // Element is in bottom half of screen
      // Ensure we leave space at both top and bottom
      return {
        maxHeight: boundsOfAnchorPoint.top - topSpace,
        top: "auto",
        bottom: Math.max(
          viewPortHeight - boundsOfAnchorPoint.bottom,
          bottomSpace
        ),
      };
    }
  }, [boundsOfAnchorPoint]);

  return createPortal(
    <StyledOptionsLightboxWrapper
      style={{
        left: isMobile
          ? "100dvw"
          : getBoundsOfAnchorPoint().right + xOffsetFromAnchorPoint,
        height: isMobile && "100svh",
        width: isMobile ? width?.mobile : width?.desktop,
        overflow,
        ...(!isMobile && { ...getDimensions() }),
      }}
      ref={ref_optionsLightboxWrapper}
      isMobile={isMobile}
      onMouseDown={e => e.stopPropagation()}
    >
      {children}
    </StyledOptionsLightboxWrapper>,
    document.getElementById("optionsLightbox-root")
  );
};
